import { useState } from "react";
import { createDatasetAssistantApi } from "datasets/components/assistant/api/DatasetAssistantApi";
import useDataset from "datasets/hooks/useDataset";
import { DatasetAccessLevel } from "datasets/types/enums/access-level";
import { Loader2, Pencil, Search, Trash2 } from "lucide-react";
import {
  SkillEditorMode,
  type SkillFormData,
  type SkillTag,
} from "~/components/assistant/SkillEditorForm";
import SkillEditorModal from "~/components/assistant/SkillEditorModal";
import type { Skill } from "~/components/assistant/types";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { Button } from "~/components/ui/button";
import { Switch } from "~/components/ui/switch";
import useErrorToast from "~/contexts/error-toast";
import { useDatasetSkills } from "~/pages/datasets/components/assistant/hooks/useDatasetSkills";
import rumClient from "~/utils/monitoring";
import SkillPickerModal from "./SkillPickerModal";

const { VITE_ANALYSIS_AGENT_API } = import.meta.env;
const api = createDatasetAssistantApi(VITE_ANALYSIS_AGENT_API);

type SkillAction = { mode: "none" } | { mode: "creating" } | { mode: "editing"; skill: Skill };

const DatasetSkillsTab = () => {
  const { data: dataset } = useDataset();
  const { showError } = useErrorToast();
  const isOwner = dataset?.access_level === DatasetAccessLevel.OWNER;

  const [isDeleting, setIsDeleting] = useState<string | null>(null);
  const [editorLoading, setEditorLoading] = useState(false);
  const [skillAction, setSkillAction] = useState<SkillAction>({ mode: "none" });

  // Delete confirmation state
  const [deletingSkillId, setDeletingSkillId] = useState<string | null>(null);

  const datasetId = dataset?.id;
  const {
    skills,
    isLoading: loading,
    error,
    toggle,
    createSkill,
    updateSkill,
    deleteSkill,
  } = useDatasetSkills(api, datasetId);
  const fetchError = !!error;

  const handleToggle = async (skillId: string, enabled: boolean) => {
    try {
      await toggle(skillId, enabled);
    } catch (e) {
      console.error(e);
      rumClient?.recordError(e);
      showError("Unable to update skill");
    }
  };

  const handleAddSkill = () => {
    setSkillAction({ mode: "creating" });
  };

  const handleEditSkill = async (skillId: string) => {
    try {
      const skill = await api.getSkill(skillId);
      setSkillAction({ mode: "editing", skill });
    } catch (e) {
      console.error(e);
      showError("Unable to load skill for editing");
    }
  };

  const handleEditorSubmit = async (data: SkillFormData) => {
    if (skillAction.mode !== "editing") return;
    setEditorLoading(true);
    try {
      await updateSkill(skillAction.skill.id, {
        description: data.description,
        content: data.content,
        tags: data.tags,
      });

      setSkillAction({ mode: "none" });
    } catch (e) {
      console.error(e);
      rumClient?.recordError(e);
      showError(e instanceof Error ? e.message : "Unable to save skill");
    } finally {
      setEditorLoading(false);
    }
  };

  const editorInitialData =
    skillAction.mode === "editing"
      ? {
          name: skillAction.skill.name,
          description: skillAction.skill.description ?? "",
          content: skillAction.skill.content,
          tags: (skillAction.skill.tags ?? []) as SkillTag[],
        }
      : undefined;

  const handleDeleteSkill = async () => {
    if (!deletingSkillId) return;
    setIsDeleting(deletingSkillId);
    try {
      await deleteSkill(deletingSkillId);
      setDeletingSkillId(null);
    } catch (e) {
      console.error(e);
      rumClient?.recordError(e);
      showError("Unable to delete skill");
    } finally {
      setIsDeleting(null);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-4">
        <Loader2 className="size-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (fetchError) {
    return (
      <p className="text-sm text-destructive">
        Failed to load dataset skills. Please try refreshing the page.
      </p>
    );
  }

  return (
    <>
      <div className="flex flex-col gap-4">
        <p className="text-sm text-muted-foreground">
          Skills provide domain-specific instructions and workflows for AI Analysis conversations on
          this dataset.
        </p>

        {skills.length > 0 && (
          <div className="flex flex-col gap-1">
            {skills.map((skill) => (
              <div key={skill.id} className="flex items-center justify-between py-2">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium">{skill.name}</p>
                  {skill.description && (
                    <p className="truncate text-[13px] text-muted-foreground">
                      {skill.description}
                    </p>
                  )}
                </div>

                <div className="flex items-center gap-1">
                  {isOwner && (
                    <>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="size-8"
                        onClick={() => handleEditSkill(skill.id)}
                        aria-label={`Edit ${skill.name}`}
                      >
                        <Pencil className="size-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="size-8"
                        onClick={() => setDeletingSkillId(skill.id)}
                        aria-label={`Delete ${skill.name}`}
                      >
                        <Trash2 className="size-4" />
                      </Button>
                    </>
                  )}
                  <Switch
                    checked={skill.enabled}
                    disabled={!isOwner}
                    onCheckedChange={(checked) => handleToggle(skill.id, checked)}
                  />
                </div>
              </div>
            ))}
          </div>
        )}

        {isOwner && (
          <Button
            variant="secondary"
            size="sm"
            className="self-start bg-gray-200 hover:bg-gray-300"
            onClick={handleAddSkill}
            aria-label="Browse skills"
          >
            <Search className="size-4" />
            Browse Skills
          </Button>
        )}
      </div>

      <AlertDialog open={!!deletingSkillId} onOpenChange={() => setDeletingSkillId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Skill?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &ldquo;
              {skills.find((s) => s.id === deletingSkillId)?.name}
              &rdquo;? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={!!isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteSkill}
              disabled={!!isDeleting}
              className="bg-destructive hover:bg-destructive/90"
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <SkillPickerModal
        api={api}
        open={skillAction.mode === "creating"}
        onClose={() => setSkillAction({ mode: "none" })}
        createSkill={createSkill}
        onCreated={() => setSkillAction({ mode: "none" })}
      />

      <SkillEditorModal
        open={skillAction.mode === "editing"}
        onClose={() => setSkillAction({ mode: "none" })}
        onSubmit={handleEditorSubmit}
        loading={editorLoading}
        mode={SkillEditorMode.EDIT}
        initialData={editorInitialData}
      />
    </>
  );
};

export default DatasetSkillsTab;
